import type * as React from "react";
import { FileText } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ActionFieldValue } from "../ActionFieldEditor";
import {
  VendorPreviewField,
  VendorPreviewShell,
} from "./VendorPreviewShell";
import { fmtCurrencyEUR, pickField, pickFieldRaw } from "./previewUtils";

export interface FicCreateInvoicePreviewProps {
  proposedFields: Readonly<Record<string, ActionFieldValue>>;
  executedFields?: Readonly<Record<string, ActionFieldValue>>;
  className?: string;
}

const asNumber = (v: unknown): number => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string" && v !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
};

/** One normalised invoice row, derived from a payload ``line_items``/``righe`` array. */
interface InvoiceRow {
  description: string;
  qty: number;
  unitPrice: number;
  vatRate: number;
  net: number;
  vat: number;
}

/** A VAT-rate bucket for the riepilogo (sum imponibile + IVA per rate). */
interface VatBucket {
  rate: number;
  net: number;
  vat: number;
}

const num = (row: Record<string, unknown>, ...keys: string[]): number => {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && row[k] !== "") return asNumber(row[k]);
  }
  return 0;
};

const str = (row: Record<string, unknown>, ...keys: string[]): string => {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "string" && v !== "") return v;
    if (typeof v === "number") return String(v);
  }
  return "";
};

/**
 * Normalise a raw ``line_items`` / ``righe`` payload array into rows. Each
 * row's net (imponibile) and VAT are taken from the payload when present and
 * otherwise derived from qty·unitPrice·rate so the table never lies about
 * provided totals.
 */
function normaliseRows(raw: unknown): InvoiceRow[] {
  if (!Array.isArray(raw)) return [];
  const rows: InvoiceRow[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) continue;
    const r = item as Record<string, unknown>;
    const description = str(r, "description", "descrizione", "name", "nome", "label");
    const qty = num(r, "qty", "quantity", "quantita", "qta") || 1;
    const unitPrice = num(r, "unit_price", "price", "prezzo", "prezzo_unitario", "importo_unitario");
    const vatRate = num(r, "vat_rate", "aliquota_iva", "iva", "tax_rate");
    const explicitNet = num(r, "net", "imponibile", "amount", "importo", "subtotal");
    const net = explicitNet || qty * unitPrice;
    const explicitVat = num(r, "vat", "iva_amount", "tax");
    const vat = explicitVat || net * (vatRate / 100);
    rows.push({ description, qty, unitPrice, vatRate, net, vat });
  }
  return rows;
}

/** Bucket rows by VAT rate for the riepilogo IVA. */
function bucketByRate(rows: InvoiceRow[]): VatBucket[] {
  const byRate = new Map<number, VatBucket>();
  for (const row of rows) {
    const b = byRate.get(row.vatRate) ?? { rate: row.vatRate, net: 0, vat: 0 };
    b.net += row.net;
    b.vat += row.vat;
    byRate.set(row.vatRate, b);
  }
  return [...byRate.values()].sort((a, b) => a.rate - b.rate);
}

/**
 * FicCreateInvoicePreview — visual preview for `fic_create_invoice`.
 *
 * Italian fattura layout: cliente + P.IVA (weighted) + numero + data, then a
 * per-row table (descrizione·qty·prezzo·IVA%·importo) with a per-rate IVA
 * riepilogo when ``line_items``/``righe`` are present, and a hero **Totale**.
 * Falls back to a single-rate ``imponibile`` view only when no array exists.
 */
export const FicCreateInvoicePreview: React.FC<FicCreateInvoicePreviewProps> = ({
  proposedFields,
  executedFields,
  className,
}) => {
  const { i18n } = useLingui();
  // Per-key resolution — never the whole-object spread (would silently drop
  // unchanged fields when only some keys are edited).
  const customerName = pickField(proposedFields, executedFields, "customer_name");
  const customerVat = pickField(proposedFields, executedFields, "customer_vat_id");
  const invoiceNumber = pickField(proposedFields, executedFields, "invoice_number");
  const issueDate = pickField(proposedFields, executedFields, "issue_date");

  const rawRows =
    pickFieldRaw(proposedFields, executedFields, "line_items") ??
    pickFieldRaw(proposedFields, executedFields, "righe");
  const rows = normaliseRows(rawRows);

  // Prefer explicit payload totals when provided.
  const payloadTotal = pickFieldRaw(proposedFields, executedFields, "total");
  const payloadVat = pickFieldRaw(proposedFields, executedFields, "vat");

  let imponibile: number;
  let iva: number;
  let buckets: VatBucket[] = [];

  if (rows.length > 0) {
    buckets = bucketByRate(rows);
    imponibile = rows.reduce((acc, r) => acc + r.net, 0);
    iva = payloadVat !== undefined ? asNumber(payloadVat) : rows.reduce((acc, r) => acc + r.vat, 0);
  } else {
    // Single-rate fallback — only when no line-item array exists.
    imponibile = asNumber(
      pickFieldRaw(proposedFields, executedFields, "imponibile") ??
        pickFieldRaw(proposedFields, executedFields, "subtotal"),
    );
    const ivaRate = asNumber(pickFieldRaw(proposedFields, executedFields, "vat_rate") ?? 22);
    iva = payloadVat !== undefined ? asNumber(payloadVat) : imponibile * (ivaRate / 100);
    buckets = [{ rate: ivaRate, net: imponibile, vat: iva }];
  }

  const totale = payloadTotal !== undefined ? asNumber(payloadTotal) : imponibile + iva;

  return (
    <VendorPreviewShell
      className={className}
      icon={<FileText className="h-4 w-4" aria-hidden />}
      title={
        invoiceNumber
          ? i18n._(msg`Fattura n. ${invoiceNumber}`)
          : i18n._(msg`Nuova fattura`)
      }
      caption={i18n._(msg`Fatture in Cloud`)}
      bottomBanner={{
        label: i18n._(msg`Totale`),
        value: (
          <span className="text-[15px] tabular-nums">{fmtCurrencyEUR(totale)}</span>
        ),
        tone: "accent",
      }}
    >
      <VendorPreviewField
        label={i18n._(msg`Cliente`)}
        wide
        value={
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-[14px] text-ink-1">
              {customerName || i18n._(msg`Cliente da definire`)}
            </span>
            {customerVat ? (
              <span className="font-mono text-[12px] text-ink-2">
                {i18n._(msg`P.IVA ${customerVat}`)}
              </span>
            ) : null}
          </div>
        }
      />
      <VendorPreviewField
        label={i18n._(msg`Numero`)}
        value={
          invoiceNumber ? (
            <span className="font-mono text-ink-1">{invoiceNumber}</span>
          ) : null
        }
      />
      <VendorPreviewField
        label={i18n._(msg`Data emissione`)}
        value={
          issueDate ? <span className="tabular-nums">{issueDate}</span> : null
        }
        hideEmpty
      />

      {rows.length > 0 ? (
        <VendorPreviewField
          label={i18n._(msg`Righe`)}
          wide
          value={
            <div className="overflow-hidden rounded-md border border-hairline">
              <table className="w-full border-collapse text-[12px]">
                <thead>
                  <tr className="bg-cream-deep/40 text-ink-3">
                    <th className="px-2 py-1.5 text-left font-medium text-[10px] uppercase tracking-wide">
                      {i18n._(msg`Descrizione`)}
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium text-[10px] uppercase tracking-wide">
                      {i18n._(msg`Qtà`)}
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium text-[10px] uppercase tracking-wide">
                      {i18n._(msg`Prezzo`)}
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium text-[10px] uppercase tracking-wide">
                      {i18n._(msg`IVA`)}
                    </th>
                    <th className="px-2 py-1.5 text-right font-medium text-[10px] uppercase tracking-wide">
                      {i18n._(msg`Importo`)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr
                      key={`${row.description}-${i}`}
                      className="border-hairline border-t text-ink-1"
                    >
                      <td className="px-2 py-1.5 text-left">
                        {row.description || (
                          <span className="text-ink-3 italic">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {row.qty}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {fmtCurrencyEUR(row.unitPrice)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {row.vatRate}%
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {fmtCurrencyEUR(row.net)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
        />
      ) : null}

      {/* Riepilogo IVA per aliquota (Totale promosso al bottomBanner). */}
      <VendorPreviewField
        label={i18n._(msg`Riepilogo IVA`)}
        wide
        value={
          <div className="flex flex-col gap-1.5 text-[12px]">
            {buckets.map((b) => (
              <div
                key={b.rate}
                className="flex items-center justify-between gap-3"
              >
                <span className="text-ink-3">
                  {i18n._(msg`Imponibile · IVA ${b.rate}%`)}
                </span>
                <span className="text-ink-2 tabular-nums">
                  {fmtCurrencyEUR(b.net)} · {fmtCurrencyEUR(b.vat)}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between gap-3 border-hairline border-t pt-1.5">
              <span className="text-ink-3">{i18n._(msg`IVA totale`)}</span>
              <span className="text-ink-2 tabular-nums">
                {fmtCurrencyEUR(iva)}
              </span>
            </div>
          </div>
        }
      />
    </VendorPreviewShell>
  );
};
FicCreateInvoicePreview.displayName = "FicCreateInvoicePreview";
